import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { UltraBasicTable } from 'components/tables/UltraBasicTable';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { LedgerTable } from 'pages/invoices/layout/record/general/LedgerTable';
import { getInvoiceTotalsAmounts } from 'pages/invoices/util/invoiceUtil';
import { ReactNode, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useGetInvoiceTotalsQuery } from 'store/api/invoices';
import { InvoiceData, InvoicePayment } from 'types/invoices';
import { t } from 'utils/getTranslations';
import { toFixed } from 'utils/numberUtil';

/** A link in a table cell reads blue, where the cell's own rule would draw it as plain text. */
const LINKED_INVOICE = 'entity-link text-link! w-fit no-underline hover:underline';

// only used internally in this file
enum InvoiceRevenueListItemType {
    INVOICE = 'INVOICE',
    INVOICE_PREPAYMENT_USED = 'INVOICE_PREPAYMENT_USED',
}

type PrepaymentUsageRow = Partial<Omit<InvoicePayment, 'amount'>> & {
    type?: InvoiceRevenueListItemType;
    amount?: number | ReactNode;
};

type Props = {
    invoice: InvoiceData;
    /** The new design's card: a ledger table with its headers, the currency in the amount's. */
    isInCard?: boolean;
};

const PrepaymentUsageCard = ({ invoice, isInCard = false }: Props) => {
    const { prepaymentsUsed: originalPrepaymentsUsed = [] } = invoice;
    const { data: invoiceTotals } = useGetInvoiceTotalsQuery(invoice.id);

    const prepaymentsUsed = useMemo(
        () => originalPrepaymentsUsed.map((el) => ({ ...el, amount: el.amount * -1 })),
        [originalPrepaymentsUsed]
    );

    const { totalWithVatPaymentCurrency } = getInvoiceTotalsAmounts(invoiceTotals);

    const invoiceRow = useMemo(
        () => ({
            date: invoice.invoiceDate,
            amount: totalWithVatPaymentCurrency,
            type: InvoiceRevenueListItemType.INVOICE,
        }),
        [invoice.invoiceDate, totalWithVatPaymentCurrency]
    );

    const columns = useMemo<DataTableColumnDef<PrepaymentUsageRow>[]>(
        () => [
            {
                accessorKey: 'date',
                cell: ({ getValue }) => getValue<PrepaymentUsageRow['date']>() ?? '',
            },
            {
                accessorKey: 'invoicePaymentTypeCode',
                cell: ({ cell }) => {
                    const { invoice, type } = cell.row.original;
                    return (
                        <div className="text-body1 text-fg-secondary">
                            {type === InvoiceRevenueListItemType.INVOICE ? (
                                t('invoices.general-tab.prepayment-usage.invoice')
                            ) : invoice ? (
                                <div className="flex flex-row gap-[0.5ch]">
                                    <RecordLink
                                        className={LINKED_INVOICE}
                                        to={`/invoice/${invoice.id}`}
                                        label={invoice.invoiceNumber}
                                        withOrigin
                                    />
                                    <div>
                                        (
                                        <PartyName
                                            party={invoice.clientParty}
                                            isLink
                                            useLinkWithOrigin
                                            variant="readonly-other"
                                        />
                                        ){' '}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                accessorKey: 'paymentNote',
                cell: ({ getValue }) => (
                    <p className="text-body1 text-fg-secondary">
                        {getValue<PrepaymentUsageRow['paymentNote']>() ?? ''}
                    </p>
                ),
                meta: {
                    width: '30%',
                },
            },
            {
                accessorKey: 'amount',
                cell: ({ getValue }) => {
                    const value = getValue<PrepaymentUsageRow['amount']>();
                    if (typeof value === 'number') {
                        return <MoneyDisplay amount={value} currency={invoice.paymentCurrencyTypeCode} displaySign />;
                    }
                    return value ?? '';
                },
            },
        ],
        [invoice.paymentCurrencyTypeCode]
    );

    const remains = [...prepaymentsUsed, invoiceRow]?.reduce(
        (accumulator, payment) => accumulator + toFixed(payment.amount, 2),
        0
    );

    const sumRow = useMemo(
        () => ({
            amount: (
                <p className="text-body1 text-fg-secondary inline-block whitespace-nowrap">
                    <FormattedMessage
                        id="invoices.remains"
                        values={{
                            amount: (
                                <span key="amount" className="text-body1 text-fg inline-block font-bold">
                                    <MoneyDisplay
                                        amount={remains}
                                        currency={invoice.paymentCurrencyTypeCode}
                                        displaySign
                                    />
                                </span>
                            ),
                        }}
                    />
                </p>
            ),
        }),
        [remains, invoice.paymentCurrencyTypeCode]
    );

    const rows = useMemo(() => {
        return [invoiceRow, ...prepaymentsUsed, sumRow];
    }, [prepaymentsUsed, invoiceRow, sumRow]);

    if (isInCard) {
        return (
            <LedgerTable
                whatHeader={<FormattedMessage id="invoices.invoice" />}
                currency={invoice.paymentCurrencyTypeCode}
                remains={remains}
                rows={[
                    {
                        key: 'invoice',
                        date: invoiceRow.date,
                        what: t('invoices.general-tab.prepayment-usage.invoice'),
                        amount: invoiceRow.amount,
                    },
                    ...prepaymentsUsed.map((used) => ({
                        key: used.id,
                        date: used.date,
                        what: used.invoice ? (
                            <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                                <RecordLink
                                    to={`/invoice/${used.invoice.id}`}
                                    label={used.invoice.invoiceNumber}
                                    withOrigin
                                />
                                <span className="inline-flex items-center">
                                    (
                                    <PartyName
                                        party={used.invoice.clientParty}
                                        isLink
                                        useLinkWithOrigin
                                        variant="readonly-other"
                                    />
                                    )
                                </span>
                            </span>
                        ) : null,
                        note: used.paymentNote,
                        amount: used.amount,
                    })),
                ]}
            />
        );
    }

    return (
        <UltraBasicTable
            columns={columns}
            data={rows}
            className="[&_td]:px-1.5 [&_td]:py-3 [&_td:first-child]:pl-0 [&_td:last-child]:pr-0 [&_td:last-child]:text-right [&_td:nth-child(4)]:text-right"
        />
    );
};

export default PrepaymentUsageCard;
